import { useMemo } from "react"
import { Alert, Button, Modal, Tabs } from "flowbite-react"
import { CheckIcon, ExclamationTriangleIcon, XCircleIcon } from "@heroicons/react/24/outline"
import { TAB_THEME } from '@/pages/Pedidos/detalhe/tab-theme'
import DetalhesImportacaoResumo from "./detalhes-importacao-resumo.component"
import DetalhesImportacaoDevolucoes from "./detalhes-importacao-devolucoes.component"
import { Link } from "react-router-dom"
import { useRegistrosContext } from "@/contexts/registros.context"

interface IDetalhesImportacaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  detalhesImportacao: any
}

const DetalhesImportacaoModal: React.FC<IDetalhesImportacaoModalProps> = ({
  isOpen,
  onClose,
  detalhesImportacao,
}) => {
  const { setIsActionsUploadOpen } = useRegistrosContext()
  const memoizedDetalhesImportacao = useMemo(() => {
    return detalhesImportacao
  }, [detalhesImportacao])

  const {
    ignorarErros,
    itensComErro,
    linkDoArquivoDeErros, 
    situacaoDaImportacao, 
  } = memoizedDetalhesImportacao || {}

  return (
    <Modal
      popup
      show={isOpen}
      onClose={onClose}
      className='flex items-center'
      size='3xl'
    >
      <Modal.Header data-testid='detalhes-importacao-modal-content'>
        <p className='text-center font-bold p-3'>Detalhes da Importação</p>
      </Modal.Header>
      <Modal.Body>
        {(situacaoDaImportacao === '3' && itensComErro === 0) && (
          <Alert color='success' rounded className='p-3 mb-5' icon={CheckIcon}>
            <div className='flex items-center justify-center text-xs'>
              <div>
                <p className="pb-1 font-bold">Planilha processada com sucesso!</p>
                <p>Sua planilha de devolução foi importada sem erros, e os dados dos
                  Trabalhadores já estão atualizados no sistema. Tudo certo!</p>
              </div>
            </div>
          </Alert>
        )}
        {(situacaoDaImportacao === '3' && itensComErro > 0 && ignorarErros === true) && (
          <Alert color='warning' rounded className='p-3 mb-5' icon={ExclamationTriangleIcon}>
            <div className='flex items-center justify-center text-xs'>
              <div>
                <p className="pb-1 font-bold">Devoluções parcialmente importadas</p>
                <p className="pb-1">
                  Foram identificados erros em alguns trabalhadores da importação. 
                  Conforme a opção selecionada, o sistema importou apenas os dados válidos.
                </p>
                
                <p>
                  Os motivos dos erros estão explicados na coluna V, {' '}
                  <Link to={linkDoArquivoDeErros} className="font-bold underline">clique aqui</Link> {' '}
                  para baixar a planilha e realizar a correção.
                </p>
              </div>
            </div>
          </Alert>
        )}
        {(situacaoDaImportacao === '2' && linkDoArquivoDeErros === '') && (
          <Alert color='failure' rounded className='p-3 mb-5' icon={XCircleIcon}>
            <div className='flex items-center justify-center text-xs'>
              <div>
                <p className="pb-1 font-bold">Erro ao processar a planilha</p>
                <p>
                  Identificamos um erro no processamento da planilha de devoluções. 
                  Infelizmente, não foi possível concluir a operação com sucesso.{" "}
                  Para seguir com o procedimento, pedimos que você tente importar 
                  novamente. Caso o problema persista, entre em contato com o 
                  suporte para que possamos ajudar.
                </p>
              </div>
            </div>
          </Alert>
        )}
        {(situacaoDaImportacao === '2' && linkDoArquivoDeErros) && (
          <Alert color='failure' rounded className='p-3 mb-5' icon={XCircleIcon}>
            <div className='flex items-center justify-center text-xs'>
              <div>
                <p className="pb-1 font-bold">Erro ao processar a planilha</p>
                <p className="pb-1">
                  Foram identificados erros em alguns trabalhadores da importação. 
                  Esses problemas impedem a importação das devoluções para o sistema.
                </p>
                <p>
                  Os motivos dos erros estão explicados na coluna V, {" "}
                  <Link to={linkDoArquivoDeErros} className="font-bold underline">clique aqui</Link> {" "}
                  para baixar a planilha e realizar a correção.
                </p>
              </div>
            </div>
          </Alert>
        )}
        <Tabs
          aria-label='Tabs with underline'
          variant='default'
          theme={TAB_THEME}
        >
          <Tabs.Item active title='Resumo'>
            <DetalhesImportacaoResumo detalhesImportacao={memoizedDetalhesImportacao} />
          </Tabs.Item>
          <Tabs.Item title='Devoluções'>
            <DetalhesImportacaoDevolucoes detalhesImportacao={memoizedDetalhesImportacao} />
          </Tabs.Item>
        </Tabs>

        <div className='flex justify-center gap-4'>
          {(situacaoDaImportacao === '2' && linkDoArquivoDeErros) && (
            <Button
              onClick={() => {
                onClose()
                setIsActionsUploadOpen(true)
              }}
              className='btn btn-primary flex justify-center bg-primary-dark border-primary-dark text-white rounded w-fit'
              id='close-button'
              data-testid='close-button'
            >
              Enviar arquivo novamente
            </Button>
          )}
          <Button
            onClick={onClose}
            className='btn btn-secondary flex justify-center bg-white border-primary-dark text-primary-dark rounded w-fit'
            id='close-button'
            data-testid='close-button'
          >
            Fechar
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default DetalhesImportacaoModal
